import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { useFaqItems } from '../hooks/useFaqItems';
import { usePageMetadata } from '../hooks/usePageMetadata';

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 20px 80px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
`;

const LastUpdated = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

const ToolsBar = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const SearchBox = styled.label`
  flex: 1;
  min-width: 240px;
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 12px 16px 12px 44px;

  svg {
    position: absolute;
    left: 16px;
    font-size: 18px;
    color: #9ca3af;
  }

  input {
    border: none;
    width: 100%;
    font-size: 14px;
    outline: none;
    color: #111827;

    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const Categories = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const CategoryChip = styled.button`
  border: 1px solid ${props => (props.active ? '#5efba9' : '#e5e7eb')};
  background: ${props => (props.active ? '#ecfdf5' : 'white')};
  color: ${props => (props.active ? '#047857' : '#374151')};
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
    color: #047857;
  }
`;

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AccordionItem = styled.div`
  border-radius: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const AccordionButton = styled.button`
  width: 100%;
  padding: 20px 24px;
  background: transparent;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  text-align: left;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    flex: 1;
  }

  svg {
    font-size: 20px;
    color: #5efba9;
    transition: transform 0.2s ease;
    transform: rotate(${props => (props.$open ? '180deg' : '0deg')});
  }
`;

const AccordionPanel = styled.div`
  padding: 0 24px 24px;
  color: #4b5563;
  line-height: 1.7;
  font-size: 15px;
`;

const Loader = styled.div`
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
  border-radius: 16px;
  background: white;
  border: 1px solid #e5e7eb;
`;

const formatDate = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_err) {
    return null;
  }
};

const FAQ = () => {
  const { items, categories: categoryMap, loading: itemsLoading, error: itemsError } = useFaqItems();
  const { metadata, loading: metadataLoading, error: metadataError } = usePageMetadata('faq');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openItems, setOpenItems] = useState(() => new Set());

  const loading = itemsLoading || metadataLoading;
  const error = itemsError || metadataError;

  const categories = useMemo(() => ['all', ...Array.from(categoryMap.keys())], [categoryMap]);

  const filteredItems = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return items.filter(item => {
      const categoryMatch = activeCategory === 'all' || (item.category || 'general') === activeCategory;
      const searchMatch = !searchTerm ||
        item.question_en.toLowerCase().includes(searchTerm) ||
        item.answer_en.toLowerCase().includes(searchTerm);
      return categoryMatch && searchMatch;
    });
  }, [items, activeCategory, search]);

  const toggleItem = (id) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Container>
      <Header>
        <Title>{metadata?.title_en ?? 'Frequently Asked Questions'}</Title>
        {metadata?.description_en && <Description>{metadata.description_en}</Description>}
        {metadata?.last_updated && (
          <LastUpdated>Last updated {formatDate(metadata.last_updated)}</LastUpdated>
        )}
      </Header>

      {loading ? (
        <Loader>Loading FAQs...</Loader>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <>
          <ToolsBar>
            <SearchBox>
              <FiSearch />
              <input
                type="search"
                placeholder="Search for a question..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </SearchBox>
            <Categories>
              {categories.map(category => (
                <CategoryChip
                  key={category}
                  type="button"
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category === 'all' ? 'All' : category.charAt(0).toUpperCase() + category.slice(1)}
                </CategoryChip>
              ))}
            </Categories>
          </ToolsBar>

          {filteredItems.length === 0 ? (
            <EmptyState>
              We couldn't find any answers that match your filters. Try adjusting your search or category.
            </EmptyState>
          ) : (
            <Accordion>
              {filteredItems.map(item => {
                const isOpen = openItems.has(item.id);
                return (
                  <AccordionItem key={item.id}>
                    <AccordionButton type="button" onClick={() => toggleItem(item.id)} $open={isOpen}>
                      <h3>{item.question_en}</h3>
                      <FiChevronDown />
                    </AccordionButton>
                    {isOpen && (
                      <AccordionPanel>
                        <p style={{ margin: 0 }}>{item.answer_en}</p>
                      </AccordionPanel>
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </>
      )}
    </Container>
  );
};

export default FAQ;

